/**
 * @file PostedEditDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-03
 * @description Asks why a posted document is being changed, before it is
 *              saved — an invoice or credit note already approved in Xero, or a
 *              billed purchase order. Shows what the save will change (when
 *              the caller knows) and takes one required line of reason, which
 *              the server keeps in the record's history and, for a Xero
 *              document, in Xero's history too.
 *
 *              The server refuses the save without a reason
 *              (EDIT_REASON_REQUIRED), so this is the friendly half of a rule
 *              that holds anyway.
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';
import './PostedEditDialog.css';

const REASON_MAX = 500;

// The body mounts with the modal's portal, so the slide-out buttons are in
// the DOM when the hook measures them (see the modal note in CLAUDE.md).
function PostedEditBody({ intro, changes, reason, setReason, error, onSubmit, saving }) {
  useSlideOutButton([saving, error]);
  return (
    <>
      {intro && <p className="posted-edit-intro">{intro}</p>}
      {Array.isArray(changes) && (
        changes.length ? (
          <ul className="posted-edit-changes">
            {changes.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        ) : (
          <p className="posted-edit-none">Nothing that changes the document itself — only details kept in this system.</p>
        )
      )}
      <label className="posted-edit-label" htmlFor="posted-edit-reason">
        Reason for the change <span className="required-field">*</span>
      </label>
      <Input
        id="posted-edit-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
        onEnter={onSubmit}
        placeholder="e.g. Customer took 18 bags, not 20"
        autoFocus
        disabled={saving}
      />
      {error && <div className="posted-edit-error">{error}</div>}
    </>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string}  props.title
 * @param {string}  [props.intro]
 * @param {string[]} [props.changes]  what will change; omit when not known
 * @param {boolean} [props.saving]
 * @param {string}  [props.confirmLabel]
 * @param {(reason: string) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
function PostedEditDialog({ isOpen, title, intro, changes, saving = false, confirmLabel = 'Save', onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) { setReason(''); setError(null); }
  }, [isOpen]);

  const submit = () => {
    const r = reason.replace(/\s+/g, ' ').trim();
    if (!r) { setError('Give a reason — one line is enough.'); return; }
    onConfirm(r);
  };

  if (!isOpen) return null;
  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onCancel}
      title={title}
      size="md"
      className="posted-edit"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={onCancel} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout btn-slideout-primary" onClick={submit} disabled={saving}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>{saving ? 'sync' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving...' : confirmLabel}</span>
            </button>
          </div>
        </div>
      }
    >
      <PostedEditBody
        intro={intro}
        changes={changes}
        reason={reason}
        setReason={(v) => { setReason(v); if (error) setError(null); }}
        error={error}
        onSubmit={submit}
        saving={saving}
      />
    </Modal>
  );
}

export default PostedEditDialog;
